import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';

import { environment } from '@env/environment';

export interface MainConfig {
  client: string;
  event_finish: boolean;
  show_gallery: boolean;
  showGalleryAlways: boolean;
  show_teaser: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService {
  // HttpBackend bypasses all HTTP interceptors (ApiPrefixInterceptor would
  // otherwise prepend environment.serverUrl to the relative assets URL)
  private http: HttpClient;

  constructor(httpBackend: HttpBackend) {
    this.http = new HttpClient(httpBackend);
  }

  load(): Promise<void> {
    // Step 1: load config.json → get client selector + deployment state
    // Step 2: load clients/{client}.json → get all event/client-specific values
    // Step 3: Object.assign patches the environment object in-place so all
    //         existing environment.xxx accesses in components continue to work
    return this.http
      .get<MainConfig>('assets/config.json')
      .toPromise()
      .then((mainConfig) => {
        if (!mainConfig) return;

        const client = mainConfig.client || 'workshop-france';

        return this.http
          .get<Record<string, any>>(`assets/clients/${client}.json`)
          .toPromise()
          .then((clientConfig) => {
            // Client config takes precedence; main config (event_finish etc.) overlays on top
            Object.assign(environment, clientConfig || {}, mainConfig);
            // ...but config.json's `client` is only a FILE SELECTOR (e.g.
            // 'bonjour-frankreich' picks bonjour-frankreich.json). The client's
            // own JSON carries the canonical logic name the app gates on, so it
            // must not be clobbered by the selector.
            if (clientConfig && clientConfig['client']) {
              environment.client = clientConfig['client'];
            }
          })
          .catch(() => {
            // Client JSON missing — apply only main config (event_finish / show_gallery)
            Object.assign(environment, mainConfig);
          });
      })
      .catch(() => {
        // config.json unreachable — keep skeleton defaults (event active, no client switch)
      })
      // Step 4: overlay the superadmin-edited welcome heading/body/year from the
      // DB-backed API (public, read-only). This runs last so environment.serverUrl
      // already reflects any client-JSON override, and it never blocks boot.
      .then(() => this.applyPublicEvent())
      // Step 5: overlay the superadmin-edited legal consent texts (Legal tab) so
      // the registration forms render them at boot. Never blocks boot.
      .then(() => this.applyPublicLegal());
  }

  /**
   * Fetch the public event identity (GET /public-event) and override the welcome
   * dialog heading/body and edition year from the DB-backed config, so text saved
   * in the Event Config UI shows on the public welcome page after a reload. Only
   * non-empty API values override the static defaults; any failure (unreachable
   * API, older API without the endpoint) is swallowed so boot is never blocked.
   */
  private applyPublicEvent(): Promise<void> {
    if (!environment.serverUrl) {
      return Promise.resolve();
    }
    return this.http
      .get<{ status: number; event?: { [key: string]: string } }>(`${environment.serverUrl}/public-event`)
      .toPromise()
      .then((res) => {
        const ev = (res && res.event) || {};
        if (ev['welcome_heading']) environment.txt_Welcome = ev['welcome_heading'];
        if (ev['welcome']) environment.txt_Welcome_Text = ev['welcome'];
        if (ev['year']) environment.event_year = ev['year'];
        if (ev['brand_logo']) environment.brand_logo = ev['brand_logo'];
        if (Array.isArray(ev['inforeisen_trips']) && ev['inforeisen_trips'].length) {
          environment.inforeisen_trips = ev['inforeisen_trips'];
        }
        if (Array.isArray(ev['inforeisen_pdfs'])) {
          environment.inforeisen_pdfs = ev['inforeisen_pdfs'];
        }
      })
      .catch(() => {
        // keep static/build-time welcome values
      });
  }

  /**
   * Fetch the editable legal consent texts (GET /public-legal) and overlay them
   * onto the environment so the visitor/exhibitor registration forms render the
   * superadmin-edited Terms / Storno / DSGVO / Media text after a reload. Any
   * failure (unreachable API, older API without the endpoint) is swallowed so
   * boot is never blocked and the forms keep their built-in wording.
   */
  private applyPublicLegal(): Promise<void> {
    if (!environment.serverUrl) {
      return Promise.resolve();
    }
    return this.http
      .get<{
        status: number;
        legal?: { [role: string]: { [type: string]: { [lang: string]: string } } };
        languages?: { [role: string]: string };
        show?: { [flag: string]: boolean };
      }>(`${environment.serverUrl}/public-legal`)
      .toPromise()
      .then((res) => {
        if (res && res.legal) environment.legal = res.legal;
        if (res && res.languages) environment.legalLanguages = res.languages;
        // Overlay the liability-waiver visibility flag so enabling it in the
        // Legal tab takes effect on reload without rebuilding the SPA.
        if (res && res.show && typeof res.show['visitor_show_haftung'] === 'boolean') {
          environment.visitor_show_haftung = res.show['visitor_show_haftung'];
        }
      })
      .catch(() => {
        // keep the forms' built-in legal text
      });
  }

  get eventFinish(): boolean {
    return environment.event_finish;
  }

  get showTeaser(): boolean {
    return environment.show_teaser;
  }
}
